import { useEffect, useMemo, useRef, useState } from 'react'
import { UNITS, formatDuration, formatTakenAt, formatTime } from '../format.js'
import { niceTicks } from './chartUtils.js'
import { Box, ChartTip, Text } from '@ui'

// Elevation, pace and (when step readings cover the walk) steps a minute along the walk, as stacked
// panels sharing one distance axis and one crosshair (measures of different scale get their own
// panel, never two y-axes). Elevation is smoothed GPS altitude (the app's GpsAltitudeSmoother) or
// the barometer's; pace and steps a minute are over the trailing minute, and the shaded clock
// minutes are brisk minutes (internal/brisk). Stored raw data is untouched: all are computed
// server-side for display.

const PANEL_HEIGHT = 130
const GAP = 28
const MARGIN = { top: 22, right: 12, bottom: 26, left: 48 }
// Slower than this (0.4 m/s, ~67 min/mi) counts as standing still, drawn as a gap.
const MIN_SPEED = 0.4
// The pointer within this many px of a photo marker (either side) picks it: wider than the marker,
// so it's easy to hit with a finger.
const PHOTO_HIT_PX = 10

// Pace ticks on whole-minute steps (1, 2, 5, 10 min...), not round numbers of seconds.
function paceTicks(min, max, count = 3) {
  const raw = (max - min) / count
  const step = [30, 60, 120, 300, 600, 900, 1800, 3600].find((s) => s >= raw) ?? 3600
  const ticks = []
  for (let t = Math.ceil(min / step) * step; t <= max + 1e-9; t += step) ticks.push(t)
  return ticks
}

function formatPaceSeconds(s) {
  const m = Math.floor(s / 60)
  const r = Math.round(s % 60)
  return r === 60 ? `${m + 1}:00` : `${m}:${String(r).padStart(2, '0')}`
}

// SVG path through the points, starting a new segment wherever the value is missing.
function linePath(points, x, y, value) {
  let d = ''
  let pen = false
  for (const p of points) {
    const v = value(p)
    if (v == null) { pen = false; continue }
    d += `${pen ? 'L' : 'M'}${x(p.d).toFixed(1)},${y(v).toFixed(1)}`
    pen = true
  }
  return d
}

// The shaded area under a line, one closed shape per unbroken run (a barometer line can start
// after the route does, when another phone's barometer is drawn on this recording).
function areaPath(points, x, y, value, bottom) {
  let d = ''
  let run = []
  const close = () => {
    if (run.length > 1) {
      d += `M${x(run[0].d).toFixed(1)},${bottom}` +
        run.map((p) => `L${x(p.d).toFixed(1)},${y(value(p)).toFixed(1)}`).join('') +
        `L${x(run[run.length - 1].d).toFixed(1)},${bottom}Z`
    }
    run = []
  }
  for (const p of points) {
    if (value(p) == null) close()
    else run.push(p)
  }
  close()
  return d
}

// The tooltip goes right of the crosshair, else left of it, so it doesn't cover the point; on a
// chart too narrow for either, it sits at the right edge.
const TIP_WIDTH = 250
function tipPosition(cx, width) {
  if (cx + 10 + TIP_WIDTH <= width) return { left: cx + 10 }
  if (cx - 10 - TIP_WIDTH >= 0) return { right: width - cx + 10 }
  return { right: 0 }
}

// Index of the point nearest distance d (points are in distance order).
function nearestIndex(points, d) {
  let lo = 0
  let hi = points.length - 1
  while (lo < hi) {
    const mid = (lo + hi) >> 1
    if (points[mid].d < d) lo = mid + 1
    else hi = mid
  }
  if (lo > 0 && d - points[lo - 1].d < points[lo].d - d) lo -= 1
  return lo
}

// The brisk minutes' line on the steps panel (internal/brisk's StepsAMinute).
const BRISK_STEPS = 100

// photos: pins from placePhotos with a distance (d); onOpenPhoto(index) opens the viewer;
// walkStart: the walk's start, so a photo time on another day shows its date; startMillis: when
// the first point was (the track's start_ms), for the clock time in the readout (0: not shown).
export default function ProfileChart({
  points, unit, hover, onHover, photos = [], onOpenPhoto, walkStart, startMillis = 0,
  elevationLabel = 'Elevation (m, GPS, smoothed)', elevationLabelShort = elevationLabel,
}) {
  const wrapRef = useRef(null)
  const [width, setWidth] = useState(600)

  useEffect(() => {
    const el = wrapRef.current
    if (!el) return undefined
    const observer = new ResizeObserver(([entry]) => setWidth(Math.max(280, entry.contentRect.width)))
    observer.observe(el)
    return () => observer.disconnect()
  }, [])

  const unitMeters = UNITS[unit].meters
  const paceOf = (p) => (p.v != null && p.v >= MIN_SPEED ? unitMeters / p.v : null)

  const scales = useMemo(() => {
    const maxD = points.length ? points[points.length - 1].d : 0
    const eles = points.map((p) => p.ele).filter((v) => v != null)
    const paces = points.map((p) => (p.v != null && p.v >= MIN_SPEED ? unitMeters / p.v : null)).filter((v) => v != null)
    const eleMin = eles.length ? Math.min(...eles) : 0
    const eleMax = eles.length ? Math.max(...eles) : 1
    // Pace: clip to the 2nd-98th percentile so one stop-start moment doesn't flatten the rest.
    const sorted = [...paces].sort((a, b) => a - b)
    const q = (f) => sorted[Math.min(sorted.length - 1, Math.floor(f * sorted.length))]
    const paceLo = sorted.length ? q(0.02) : 600
    const paceHi = sorted.length ? q(0.98) : 1200
    const spms = points.map((p) => p.spm).filter((v) => v != null)
    // Steps from 0, to at least a little over the brisk line; a sprint's peak may clip.
    const spmSorted = [...spms].sort((a, b) => a - b)
    const spmHi = spmSorted.length ? spmSorted[Math.min(spmSorted.length - 1, Math.floor(0.99 * spmSorted.length))] : 0
    return {
      maxD,
      ele: { min: eleMin - 2, max: eleMax + 2, has: eles.length > 0 },
      pace: { min: paceLo, max: Math.max(paceHi, paceLo + 30), has: paces.length > 0 },
      steps: { min: 0, max: Math.max(130, Math.ceil((spmHi + 5) / 10) * 10), has: spms.length > 0 },
    }
  }, [points, unitMeters])

  const innerW = width - MARGIN.left - MARGIN.right
  const x = (d) => MARGIN.left + (scales.maxD ? (d / scales.maxD) * innerW : 0)
  const eleTop = MARGIN.top
  const paceTop = MARGIN.top + PANEL_HEIGHT + GAP
  const eleY = (v) => eleTop + PANEL_HEIGHT - ((v - scales.ele.min) / (scales.ele.max - scales.ele.min)) * PANEL_HEIGHT
  // Faster pace (fewer seconds per unit) is drawn higher.
  const paceY = (s) => {
    const clamped = Math.min(scales.pace.max, Math.max(scales.pace.min, s))
    return paceTop + ((clamped - scales.pace.min) / (scales.pace.max - scales.pace.min)) * PANEL_HEIGHT
  }
  const stepsTop = paceTop + PANEL_HEIGHT + GAP
  const stepsY = (v) => stepsTop + PANEL_HEIGHT - (Math.min(scales.steps.max, Math.max(0, v)) / scales.steps.max) * PANEL_HEIGHT
  const bottom = (scales.steps.has ? stepsTop : paceTop) + PANEL_HEIGHT
  const height = bottom + MARGIN.bottom

  const xTicks = niceTicks(0, scales.maxD / unitMeters, Math.max(2, Math.floor(innerW / 90)))
  const eleTicks = niceTicks(scales.ele.min, scales.ele.max, 3)
  const paceTickValues = paceTicks(scales.pace.min, scales.pace.max, 3)

  const elePath = linePath(points, x, eleY, (p) => p.ele)
  const eleArea = areaPath(points, x, eleY, (p) => p.ele, eleTop + PANEL_HEIGHT)
  const pacePath = linePath(points, x, paceY, paceOf)
  const stepsTicks = niceTicks(0, scales.steps.max, 3)
  const stepsPath = scales.steps.has ? linePath(points, x, stepsY, (p) => p.spm) : ''
  // Brisk minutes as bands behind the line: one per run of points in brisk clock minutes, out to
  // halfway to the neighbouring points.
  const briskBands = []
  if (scales.steps.has) {
    let from = null
    points.forEach((p, i) => {
      if (p.brisk && from == null) from = i
      if (from != null && (!p.brisk || i === points.length - 1)) {
        const to = p.brisk ? i : i - 1
        const left = from > 0 ? (points[from - 1].d + points[from].d) / 2 : points[from].d
        const right = to < points.length - 1 ? (points[to].d + points[to + 1].d) / 2 : points[to].d
        briskBands.push({ x: x(left), w: Math.max(1, x(right) - x(left)) })
        from = null
      }
    })
  }

  // Photos on the elevation line (on the panel's floor where there's no altitude), in time order.
  const photoMarks = photos.map((pin) => {
    const ele = points[nearestIndex(points, pin.d)]?.ele
    return { pin, x: x(pin.d), y: ele != null && scales.ele.has ? eleY(ele) : eleTop + PANEL_HEIGHT }
  })
  const marksNear = (px) => photoMarks
    .filter((m) => Math.abs(m.x - px) <= PHOTO_HIT_PX)
    .sort((a, b) => Math.abs(a.x - px) - Math.abs(b.x - px))
  const [pointerX, setPointerX] = useState(null)

  const handleMove = (e) => {
    if (!points.length) return
    const rect = e.currentTarget.getBoundingClientRect()
    const px = e.clientX - rect.left
    setPointerX(px)
    const d = ((px - MARGIN.left) / innerW) * scales.maxD
    onHover(nearestIndex(points, Math.min(scales.maxD, Math.max(0, d))))
  }
  const handleClick = (e) => {
    const [mark] = marksNear(e.clientX - e.currentTarget.getBoundingClientRect().left)
    if (mark) onOpenPhoto?.(mark.pin.index)
  }

  const hp = hover != null ? points[hover] : null
  const hPace = hp ? paceOf(hp) : null
  const hClock = hp && startMillis ? formatTime(startMillis + hp.t * 1000) : null
  const hPhotos = hp && pointerX != null ? marksNear(pointerX) : []

  if (!points.length) return null

  return (
    <Box ref={wrapRef} position="relative">
      <svg
        width={width} height={height} role="img"
        aria-label={`Elevation${scales.steps.has ? ', pace and steps a minute' : ' and pace'} along the walk; the splits table below has the same distances and times`}
        onPointerMove={handleMove} onPointerLeave={() => { onHover(null); setPointerX(null) }} onClick={handleClick}
        style={{ touchAction: 'pan-y', display: 'block', cursor: hPhotos.length ? 'pointer' : undefined }}
      >
        {/* Panel titles name the single series in each, so no legend box is needed. */}
        <text x={MARGIN.left} y={eleTop} dy="-0.1em" fill="var(--wl-muted)" style={{ fontSize: 12 }}>
          {/* A narrow chart has no room for the long title beside the photo key. */}
          {width < 560 ? elevationLabelShort : elevationLabel}
        </text>
        <text x={MARGIN.left} y={paceTop} dy="-0.4em" fill="var(--wl-muted)" style={{ fontSize: 12 }}>
          Pace (min{UNITS[unit].paceLabel}, faster higher)
        </text>
        {scales.steps.has && (
          <text x={MARGIN.left} y={stepsTop} dy="-0.4em" fill="var(--wl-muted)" style={{ fontSize: 12 }}>
            {width < 560 ? 'Steps a minute (brisk shaded)' : `Steps a minute (brisk minutes shaded: ${BRISK_STEPS} steps or more)`}
          </text>
        )}

        {photoMarks.length > 0 && (
          <g aria-hidden="true">
            <circle cx={width - MARGIN.right - 4} cy={eleTop - 5} r="4" fill="var(--wl-emphasis)" stroke="var(--wl-surface)" strokeWidth="2" />
            <text x={width - MARGIN.right - 12} y={eleTop} dy="-0.1em" textAnchor="end" fill="var(--wl-muted)" style={{ fontSize: 12 }}>
              {photoMarks.some((m) => m.pin.photo.media_type === 'video') ? 'Photo or video' : 'Photo'}
            </text>
          </g>
        )}

        {/* Gridlines and y ticks */}
        {scales.ele.has && eleTicks.map((t) => (
          <g key={`e${t}`}>
            <line x1={MARGIN.left} x2={width - MARGIN.right} y1={eleY(t)} y2={eleY(t)} stroke="var(--chart-grid)" strokeWidth="1" />
            <text x={MARGIN.left - 6} y={eleY(t)} dy="0.32em" textAnchor="end" fill="var(--wl-muted)" style={{ fontSize: 11 }}>{Math.round(t)}</text>
          </g>
        ))}
        {scales.pace.has && paceTickValues.map((t) => (
          <g key={`p${t}`}>
            <line x1={MARGIN.left} x2={width - MARGIN.right} y1={paceY(t)} y2={paceY(t)} stroke="var(--chart-grid)" strokeWidth="1" />
            <text x={MARGIN.left - 6} y={paceY(t)} dy="0.32em" textAnchor="end" fill="var(--wl-muted)" style={{ fontSize: 11 }}>{formatPaceSeconds(t)}</text>
          </g>
        ))}

        {scales.steps.has && (
          <>
            {briskBands.map((b) => (
              <rect key={`b${b.x}`} x={b.x} y={stepsTop} width={b.w} height={PANEL_HEIGHT} fill="var(--chart-steps)" fillOpacity="0.14" />
            ))}
            {stepsTicks.map((t) => (
              <g key={`s${t}`}>
                <line x1={MARGIN.left} x2={width - MARGIN.right} y1={stepsY(t)} y2={stepsY(t)} stroke="var(--chart-grid)" strokeWidth="1" />
                <text x={MARGIN.left - 6} y={stepsY(t)} dy="0.32em" textAnchor="end" fill="var(--wl-muted)" style={{ fontSize: 11 }}>{Math.round(t)}</text>
              </g>
            ))}
            <line
              x1={MARGIN.left} x2={width - MARGIN.right} y1={stepsY(BRISK_STEPS)} y2={stepsY(BRISK_STEPS)}
              stroke="var(--wl-muted)" strokeWidth="1" strokeDasharray="4 3"
            />
            <text x={width - MARGIN.right} y={stepsY(BRISK_STEPS)} dy="-0.35em" textAnchor="end" fill="var(--wl-muted)" style={{ fontSize: 11 }}>{BRISK_STEPS}</text>
            <path d={stepsPath} fill="none" stroke="var(--chart-steps)" strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />
          </>
        )}

        {/* Shared distance axis under the lowest panel */}
        {xTicks.map((t) => (
          <text key={`x${t}`} x={x(t * unitMeters)} y={height - 8} textAnchor="middle" fill="var(--wl-muted)" style={{ fontSize: 11 }}>
            {t}{t === xTicks[xTicks.length - 1] ? ` ${UNITS[unit].label}` : ''}
          </text>
        ))}

        {scales.ele.has ? (
          <>
            <path d={eleArea} fill="var(--chart-elevation)" fillOpacity="0.1" stroke="none" />
            <path d={elePath} fill="none" stroke="var(--chart-elevation)" strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />
          </>
        ) : (
          <text x={MARGIN.left + 8} y={eleTop + PANEL_HEIGHT / 2} fill="var(--wl-muted)" style={{ fontSize: 12 }}>No altitude recorded</text>
        )}
        {scales.pace.has ? (
          <path d={pacePath} fill="none" stroke="var(--chart-pace)" strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />
        ) : (
          <text x={MARGIN.left + 8} y={paceTop + PANEL_HEIGHT / 2} fill="var(--wl-muted)" style={{ fontSize: 12 }}>Not enough points for pace</text>
        )}

        {/* Photo markers: neutral ink with a surface ring, so they read over the line and area. */}
        {photoMarks.map((m) => (
          <circle
            key={m.pin.photo.id} cx={m.x} cy={m.y} r={hPhotos.includes(m) ? 6 : 5}
            fill="var(--wl-emphasis)" stroke="var(--wl-surface)" strokeWidth="2" pointerEvents="none"
          />
        ))}

        {/* Crosshair, snapped to the nearest point, across both panels */}
        {hp && (
          <g pointerEvents="none">
            <line x1={x(hp.d)} x2={x(hp.d)} y1={eleTop} y2={bottom} stroke="var(--wl-muted)" strokeWidth="1" />
            {hp.ele != null && <circle cx={x(hp.d)} cy={eleY(hp.ele)} r="4" fill="var(--chart-elevation)" stroke="var(--wl-surface)" strokeWidth="2" />}
            {hPace != null && <circle cx={x(hp.d)} cy={paceY(hPace)} r="4" fill="var(--chart-pace)" stroke="var(--wl-surface)" strokeWidth="2" />}
            {scales.steps.has && hp.spm != null && <circle cx={x(hp.d)} cy={stepsY(hp.spm)} r="4" fill="var(--chart-steps)" stroke="var(--wl-surface)" strokeWidth="2" />}
          </g>
        )}
      </svg>

      {hp && (
        <ChartTip
          style={{ top: 0, pointerEvents: 'none', minWidth: 150, maxWidth: TIP_WIDTH, ...tipPosition(x(hp.d), width) }}
        >
          <Box weight="semibold">
            {(hp.d / unitMeters).toFixed(2)} {UNITS[unit].label}{hClock && ` · ${hClock}`} · {formatDuration(hp.t * 1000)} elapsed
          </Box>
          <div><span style={{ color: 'var(--chart-elevation)' }}>●</span> Elevation {hp.ele == null ? '–' : `${Math.round(hp.ele)} m`}</div>
          <div><span style={{ color: 'var(--chart-pace)' }}>●</span> Pace {hPace == null ? '–' : `${formatPaceSeconds(hPace)} ${UNITS[unit].paceLabel}`}</div>
          {scales.steps.has && (
            <div>
              <span style={{ color: 'var(--chart-steps)' }}>●</span> Steps {hp.spm == null ? '–' : `${Math.round(hp.spm)} a minute`}
              {hp.brisk && ' · brisk minute'}
            </div>
          )}
          {hPhotos.length > 0 && (
            <div>
              <span style={{ color: 'var(--wl-emphasis)' }}>●</span>{' '}
              {hPhotos.length === 1
                ? `${hPhotos[0].pin.photo.media_type === 'video' ? 'Video' : 'Photo'}${hPhotos[0].pin.photo.taken_at ? ` taken ${formatTakenAt(hPhotos[0].pin.photo.taken_at, walkStart)}` : ''}`
                : `${hPhotos.length} photos here`}
              <Text muted> · click to open</Text>
            </div>
          )}
        </ChartTip>
      )}
    </Box>
  )
}
